import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Header } from './components/Header';
import { LiveStatusBanner } from './components/LiveStatusBanner';
import { LiveStatusProvider } from './components/LiveStatusProvider';
import { ToastProvider } from './components/Toast';

// The job drawer (/jobs/:id) renders as a modal OVER the jobs list, so hopping
// list ↔ drawer must keep the list's scroll position. Every other pathname is
// a distinct screen and should start at the top.
function scrollKey(pathname: string): string {
  return /^\/jobs\/[^/]+$/.test(pathname) ? '/' : pathname;
}

// Full-height app shell: a flex column owning the viewport. The header is fixed
// height; <main> takes the rest and is the scroll container (so the run page can
// claim h-full and fill the window, while list/admin pages scroll within main).
export default function App() {
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const key = scrollKey(pathname);

  // <main> is a persistent element-level scroll container (the window itself
  // never scrolls), so the data router's <ScrollRestoration/> — which manages
  // window scroll — doesn't apply. Without this reset, a long jobs-list scroll
  // carries over to /me and /admin.
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [key]);

  return (
    // ToastProvider wraps LiveStatusProvider, not the other way round: the live-status
    // provider raises the transient-degradation toast, so it must be able to `useToast()`.
    <ToastProvider>
      <LiveStatusProvider>
        <div className="flex h-screen flex-col bg-slate-50">
          <Header />
          {/* Between header and <main> so it is always visible, never scrolled away — a
              banner inside the scroll container would hide the moment the user scrolls. */}
          <LiveStatusBanner />
          <main ref={mainRef} className="min-h-0 flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </LiveStatusProvider>
    </ToastProvider>
  );
}
